import classNames from "classnames";
import find from "lodash/find";
import React, { CSSProperties, FunctionComponent } from "react";
import { v1 as uuid } from "uuid";
import { fontTagToCssClass } from "../../../../agreed/fonts";
import useCustomizationContext from "../../../../hooks/useCustomizationContext";
import { Content, ContentPage } from "../../../../types/customization/content";
import { Template, TemplatePageDefinition } from "../../../../types/customization/template";
import { lengthMmToPx, sizeMmToPx } from "../../../../utils/customization/box";
import {
  findColorDefinition,
  findEditableTextDefinition,
  findFontColorDefinition,
  findFontDefinition,
} from "../../../../utils/customization/finders";
import { findLocalizedContent } from "../../../../utils/customization/localized";
import {
  calcTextProperties,
  DEFAULT_FONT_COLOR,
  textAlignToCss,
  textOrientationToCss,
} from "../../../../utils/customization/text";
import AdvancedTextArea from "../../../atoms/customization/AdvancedTextArea";
import "./style.scss";

interface Props {
  content: Content;
  elementDefinitionKey: string;
  page: ContentPage;
  pageDefinition: TemplatePageDefinition;
  ppmm: number;
  template: Template;
}

const EditableText: FunctionComponent<Props> = ({
  content,
  elementDefinitionKey,
  page,
  pageDefinition,
  ppmm,
  template,
}) => {
  const {
    dispatch,
    ui: { defaultFontKey, editedText },
  } = useCustomizationContext();
  const pageKey = page.key;

  const elementDefinition = findEditableTextDefinition(elementDefinitionKey, pageDefinition);
  const element = find(
    page.editableTexts,
    ({ editableTextKey }) => editableTextKey === elementDefinition.key
  );

  const prefilledText =
    !element && elementDefinition.preFilledText
      ? findLocalizedContent(elementDefinition.preFilledText, content.locale)
      : undefined;
  const text = element ? element.content : "";

  const defaultFontDefinition = defaultFontKey
    ? findFontDefinition(defaultFontKey, template)
    : undefined;
  const fontDefinition = element
    ? findFontDefinition(element.fontKey, template)
    : defaultFontDefinition || template.fontDefinitions[0];

  const { boxSize, fontSizeLength, lineHeightLength, maxLines } = calcTextProperties(
    elementDefinition,
    fontDefinition,
    pageDefinition
  );

  const style: CSSProperties = {
    ...textAlignToCss(elementDefinition.textAlign),
    ...textOrientationToCss(elementDefinition.textOrientation, sizeMmToPx(boxSize, ppmm)),
  };

  const pageColorDefinition = template.colorDefinitions.find(
    (colorDefinition) =>
      colorDefinition.key === page.backgroundColorKey &&
      pageDefinition.backgroundColorKeys.includes(colorDefinition.key)
  );
  const defaultColorDefinition = findColorDefinition(
    pageDefinition.backgroundColorKeys[0],
    template.colorDefinitions
  );
  const pageFontColorDefinition = pageColorDefinition
    ? findFontColorDefinition(elementDefinition.fontColorKey, pageColorDefinition)
    : undefined;
  const defaultFontColorDefinition = defaultColorDefinition
    ? findFontColorDefinition(elementDefinition.fontColorKey, defaultColorDefinition)
    : undefined;
  const defaultColor = defaultFontColorDefinition?.value || DEFAULT_FONT_COLOR;

  const contentStyle: CSSProperties = {
    color: pageFontColorDefinition?.value || defaultColor,
    fontSize: `${lengthMmToPx(fontSizeLength, ppmm).value}px`,
    lineHeight: `${Math.floor(lengthMmToPx(lineHeightLength, ppmm).value)}px`, // floor fix difference with chrome and safari which cut value after point
  };

  const placeholder = findLocalizedContent(elementDefinition.placeholder, content.locale);

  const onChange = (newText: string) => {
    if (newText !== text) {
      dispatch({
        content: newText,
        editableTextKey: elementDefinition.key,
        pageKey,
        type: "changeEditableTextElementContent",
        undoKey: editedText ? editedText.undoKey : "ignore",
      });
    }
  };

  const onClick = () => {
    if (
      editedText?.elementDefinitionKey !== elementDefinitionKey ||
      editedText?.pageKey !== pageKey
    ) {
      const newKey = `textEditor${uuid()}`; // create new key for textEditor reopening
      dispatch({
        editedText: {
          elementDefinitionKey,
          pageKey,
          undoKey: newKey,
        },
        type: "setEditedText",
      });
    }
  };

  if (!element && prefilledText) {
    onChange(prefilledText.content);
  }

  // Hack. Use UseLayoutEffect and a ref instead.
  const focus =
    editedText &&
    pageKey === editedText.pageKey &&
    elementDefinitionKey === editedText.elementDefinitionKey;

  return (
    <div className="EditableText" onClick={onClick} style={style}>
      <AdvancedTextArea
        className={classNames("EditableText-content", fontTagToCssClass(fontDefinition.fontTag))}
        focus={focus}
        maxRows={maxLines}
        onChange={onChange}
        placeholder={placeholder.content}
        style={contentStyle}
        value={text}
      />
    </div>
  );
};

export default EditableText;
